import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import {
  CalendarToday,
  Close,
  Inventory2Outlined,
  Laptop,
  PersonAddAlt1,
  QrCode2,
  WarningAmber,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import ApiCalls from "services/flick_erp_api_call";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { useMaterialUIController } from "context";

const formatDate = (value) => {
  if (!value) return "Not available";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "Not available";
};

const hasAnyActiveAssignment = (item) =>
  (item?.assignments || []).some((assignment) => !assignment?.unassigned_date);

const getStatusColor = ({ warranty_till, status }) => {
  if (warranty_till && dayjs(warranty_till).isBefore(dayjs(), "day")) return "warning";
  if (status === "assigned") return "info";
  return "success";
};

const SystemDetails = ({ data }) => {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;
  const loggedUser = JSON.parse(localStorage.getItem("user"));

  const [assignments, setAssignments] = useState([]);
  const [hardwareItems, setHardwareItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openUnassignDrawer, setOpenUnassignDrawer] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [unassignForm, setUnassignForm] = useState({
    unassigned_date: dayjs().format("YYYY-MM-DD"),
    unassigned_reason: "",
  });
  const [newRecord, setNewRecord] = useState({
    hardware_item_id: "",
    name: "",
    brand: "",
    serial_number: "",
    assigned_date: dayjs().format("YYYY-MM-DD"),
  });

  const ui = {
    card: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : "#f8fafc",
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.12),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
    muted: darkMode ? rgba(palette.text.main, 0.72) : rgba(colors.text.main, 0.8),
    shadow: darkMode ? "0 18px 40px rgba(2, 6, 23, 0.28)" : `0 18px 40px ${rgba(colors.dark.main, 0.06)}`,
  };

  const loadSystemDetails = async () => {
    if (!data?.id) return;

    setLoading(true);
    try {
      const [assignmentRes, hardwareRes] = await Promise.all([
        ApiCalls.getInventoryHardwareAssignments(data.id),
        ApiCalls.getHardwareItems(),
      ]);

      setAssignments(Array.isArray(assignmentRes?.data) ? assignmentRes.data : []);
      setHardwareItems(Array.isArray(hardwareRes?.data) ? hardwareRes.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSystemDetails();
  }, [data?.id]);

  const hardwareById = useMemo(() => {
    const map = new Map();
    for (const item of hardwareItems) map.set(String(item?.id), item);
    return map;
  }, [hardwareItems]);

  const assignedAssets = useMemo(() => {
    return assignments
      .filter((a) => !a?.unassigned_date)
      .map((a) => {
        const hardwareId = a?.hardware_item_id ?? a?.hardware_item;
        const hardware = hardwareById.get(String(hardwareId));
        return {
          assignment: a,
          hardware,
          hardwareId,
          name: a?.hardware_item_info?.name || hardware?.name,
          brand: a?.hardware_item_info?.brand || hardware?.brand,
          serial_number: a?.hardware_item_info?.serial_number || hardware?.serial_number,
        };
      });
  }, [assignments, hardwareById]);

  const availableAssets = useMemo(
    () => hardwareItems.filter((item) => !hasAnyActiveAssignment(item) && item?.status === "in_stock"),
    [hardwareItems]
  );

  const summary = useMemo(() => {
    const total = assignedAssets.length;
    const expired = assignedAssets.filter(
      ({ hardware }) => hardware?.warranty_till && dayjs(hardware.warranty_till).isBefore(dayjs(), "day")
    ).length;
    const expiringSoon = assignedAssets.filter(({ hardware }) => {
      if (!hardware?.warranty_till) return false;
      const diff = dayjs(hardware.warranty_till).diff(dayjs(), "day");
      return diff >= 0 && diff <= 30;
    }).length;

    return { total, expired, expiringSoon };
  }, [assignedAssets]);

  const handleDrawerInputChange = (field, value) => {
    setNewRecord((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenDrawer = () => {
    setNewRecord({
      hardware_item_id: "",
      name: "",
      brand: "",
      serial_number: "",
      assigned_date: dayjs().format("YYYY-MM-DD"),
    });
    setOpenDrawer(true);
  };

  const handleAssignAsset = async () => {
    if (!newRecord.hardware_item_id || !newRecord.assigned_date || !data?.id) return;

    setSaving(true);
    try {
      const payload = {
        hardware_item_info: {
          name: newRecord.name,
          brand: newRecord.brand,
          serial_number: newRecord.serial_number,
        },
        hardware_item_id: newRecord.hardware_item_id,
        hardware_item: newRecord.hardware_item_id,
        employee: data.id,
        assigned_date: newRecord.assigned_date,
        assigned_by: loggedUser?.employee_id || data.id,
      };

      const res = await ApiCalls.postInventoryHardwareAssignments(payload);
      if (res?.code === 201) {
        setOpenDrawer(false);
        await loadSystemDetails();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignInputChange = (field, value) => {
    setUnassignForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenUnassignDrawer = async (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
    setSelectedAssignment(null);
    setUnassignForm({
      unassigned_date: dayjs().format("YYYY-MM-DD"),
      unassigned_reason: "",
    });
    setOpenUnassignDrawer(true);

    const res = await ApiCalls.getInventoryHardwareAssignmentById(assignmentId);
    if (res?.success && res?.code === 200) {
      setSelectedAssignment(res.data);
      setUnassignForm({
        unassigned_date: res.data?.unassigned_date || dayjs().format("YYYY-MM-DD"),
        unassigned_reason: res.data?.unassigned_reason || "",
      });
    }
  };

  const handleConfirmUnassign = async () => {
    if (!selectedAssignmentId) return;

    setSaving(true);
    try {
      const payload = {
        unassigned_date: unassignForm.unassigned_date,
        unassigned_reason: unassignForm.unassigned_reason,
      };

      const res = await ApiCalls.updateInventoryHardwareAssignments(selectedAssignmentId, payload);
      if (res?.success || res?.code === 200) {
        setOpenUnassignDrawer(false);
        setSelectedAssignmentId(null);
        setSelectedAssignment(null);
        await loadSystemDetails();
      }
    } finally {
      setSaving(false);
    }
  };

  const StatCard = ({ icon, label, value, subtitle }) => (
    <Card
      sx={{
        p: 2,
        borderRadius: "20px",
        boxShadow: "none",
        border: `1px solid ${ui.border}`,
        background: ui.soft,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
            color: colors.white.main,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <MDTypography variant="button" fontWeight="medium" sx={{ color: ui.muted }}>
            {label}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: ui.title, lineHeight: 1.2 }}>
            {value}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: ui.muted }}>
            {subtitle}
          </MDTypography>
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Card
      sx={{
        p: 1.5,
        m: 1,
        borderRadius: "28px",
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        boxShadow: ui.shadow,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <MDBox display="flex" justifyContent="center" gap={2}>
          <MDBox
            sx={{
              width: 52,
              height: 52,
              borderRadius: "16px",
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
              color: colors.white.main,
            }}
          >
            <Laptop />
          </MDBox>
          <MDBox>
            <MDTypography variant="h5" sx={{ color: ui.title }}>
              System Details
            </MDTypography>
            <MDTypography variant="body2" sx={{ mt: 0.35, color: ui.muted }}>
              Track the assets currently assigned to this employee.
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDButton size="small" color="info" sx={{ borderRadius: "14px" }} onClick={handleOpenDrawer}>
          Assign Asset
        </MDButton>
      </MDBox>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<Inventory2Outlined fontSize="small" />}
            label="Assigned Assets"
            value={summary.total}
            subtitle="Items linked to this employee"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<CalendarToday fontSize="small" />}
            label="Warranty Expiring"
            value={summary.expiringSoon}
            subtitle="Within the next 30 days"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<WarningAmber fontSize="small" />}
            label="Warranty Expired"
            value={summary.expired}
            subtitle="Needs admin attention"
          />
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 380,
            margin: "0 !important",
            height: "100%",
            borderRadius: 0,
            boxShadow: 3,
            bgcolor: ui.soft,
            borderLeft: `1px solid ${ui.border}`,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          <Box>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: ui.title }}>
              Assign Asset
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              Link an available inventory item to this employee profile.
            </MDTypography>
          </Box>
          <IconButton onClick={() => setOpenDrawer(false)}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ width: 380, maxWidth: "100%", p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Select Item
            </MDTypography>
            <Autocomplete
              options={availableAssets}
              getOptionLabel={(option) =>
                [option?.name, option?.serial_number ? `(${option.serial_number})` : ""]
                  .filter(Boolean)
                  .join(" ")
              }
              value={
                availableAssets.find(
                  (item) => String(item.id) === String(newRecord.hardware_item_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDrawerInputChange("hardware_item_id", newValue?.id || "");
                handleDrawerInputChange("name", newValue?.name || "");
                handleDrawerInputChange("brand", newValue?.brand || "");
                handleDrawerInputChange("serial_number", newValue?.serial_number || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={availableAssets.length ? "Choose an available asset" : "No available assets"}
                />
              )}
              noOptionsText="No unassigned assets available"
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Category
            </MDTypography>
            <TextField value={newRecord.brand || ""} fullWidth InputProps={{ readOnly: true }} />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Serial Number
            </MDTypography>
            <TextField value={newRecord.serial_number || ""} fullWidth InputProps={{ readOnly: true }} />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Assigned Date
            </MDTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={newRecord.assigned_date ? dayjs(newRecord.assigned_date) : null}
                onChange={(newVal) =>
                  handleDrawerInputChange(
                    "assigned_date",
                    newVal ? newVal.format("YYYY-MM-DD") : ""
                  )
                }
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "YYYY-MM-DD",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Alert severity="info" sx={{ borderRadius: "14px" }}>
            The selected asset will be assigned directly to this employee.
          </Alert>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <MDButton
              size="small"
              variant="contained"
              color="info"
              onClick={handleAssignAsset}
              disabled={!newRecord.hardware_item_id || !newRecord.assigned_date || saving}
            >
              {saving ? "Assigning..." : "Assign Asset"}
            </MDButton>
            <MDButton size="small" variant="outlined" onClick={() => setOpenDrawer(false)}>
              Cancel
            </MDButton>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={openUnassignDrawer}
        onClose={() => setOpenUnassignDrawer(false)}
        PaperProps={{
          sx: {
            width: 380,
            margin: "0 !important",
            height: "100%",
            borderRadius: 0,
            boxShadow: 3,
            bgcolor: ui.soft,
            borderLeft: `1px solid ${ui.border}`,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          <Box>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: ui.title }}>
              Unassign Asset
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              Record why this asset is being unassigned from this employee.
            </MDTypography>
          </Box>
          <IconButton onClick={() => setOpenUnassignDrawer(false)}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ width: 380, maxWidth: "100%", p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: "18px",
              boxShadow: "none",
              border: `1px solid ${ui.border}`,
              background: ui.card,
            }}
          >
            <MDTypography variant="button" fontWeight="medium" sx={{ color: ui.muted }}>
              Asset
            </MDTypography>
            <MDTypography variant="h6" sx={{ color: ui.title, mt: 0.25 }}>
              {selectedAssignment?.hardware_item_info?.name || "Loading..."}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              Serial No: {selectedAssignment?.hardware_item_info?.serial_number || "Not available"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              Assigned on: {formatDate(selectedAssignment?.assigned_date)}
            </MDTypography>
          </Card>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Unassigned Date
            </MDTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={unassignForm.unassigned_date ? dayjs(unassignForm.unassigned_date) : null}
                onChange={(newVal) =>
                  handleUnassignInputChange(
                    "unassigned_date",
                    newVal ? newVal.format("YYYY-MM-DD") : ""
                  )
                }
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "YYYY-MM-DD",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Reason
            </MDTypography>
            <TextField
              value={unassignForm.unassigned_reason}
              onChange={(e) => handleUnassignInputChange("unassigned_reason", e.target.value)}
              placeholder="e.g. Returned to stock, Replacement issued, Repair, Resigned..."
              fullWidth
              multiline
              minRows={3}
            />
          </Box>

          <Alert severity="warning" sx={{ borderRadius: "14px" }}>
            This will mark the assignment as unassigned and it will no longer show under this employee.
          </Alert>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <MDButton
              size="small"
              variant="contained"
              color="warning"
              onClick={handleConfirmUnassign}
              disabled={!unassignForm.unassigned_date || saving}
            >
              {saving ? "Unassigning..." : "Confirm Unassign"}
            </MDButton>
            <MDButton size="small" variant="outlined" onClick={() => setOpenUnassignDrawer(false)}>
              Cancel
            </MDButton>
          </Box>
        </Box>
      </Drawer>

      {loading ? (
        <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : assignedAssets.length === 0 ? (
        <Card
          sx={{
            mt: 2,
            p: 3,
            borderRadius: "20px",
            boxShadow: "none",
            border: `1px dashed ${ui.border}`,
            background: ui.soft,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h6" sx={{ color: ui.title }}>
            No assets assigned yet
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.5 }}>
            Assign inventory items here so HR can easily track what this employee is using.
          </MDTypography>
        </Card>
      ) : (
        <Grid container spacing={2} mt={0.5}>
          {assignedAssets.map(({ assignment, hardware, name, brand, serial_number }) => {
            const warrantyExpired =
              !!hardware?.warranty_till && dayjs(hardware.warranty_till).isBefore(dayjs(), "day");

            return (
              <Grid item xs={12} md={6} key={assignment?.id}>
                <Card
                  sx={{
                    p: 2,
                    height: "100%",
                    borderRadius: "22px",
                    boxShadow: "none",
                    border: `1px solid ${ui.border}`,
                    background: ui.soft,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      {/*<Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
                        <Chip label={brand || "Asset"} size="small" variant="outlined" sx= {{color : ui.title}}/>
                        <Chip
                          label={warrantyExpired ? "Warranty Expired" : "Assigned"}
                          size="small"
                          color={getStatusColor({ warranty_till: hardware?.warranty_till, status: hardware?.status })}
                        />
                      </Stack>*/}
											<MDBox sx={{display : 'flex', gap : "6px"}}>
												<MDTypography variant="h6" sx={{ color: ui.title }}>
													{name || "Unnamed asset"}
												</MDTypography>
												<Chip label={brand || "Asset"} size="small" variant="outlined" sx= {{color : ui.title}}/>
											</MDBox>
                      <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.35 }}>
                        Serial No: {serial_number || "Not available"}
                      </MDTypography>
                    </Box>

                    <MDButton
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleOpenUnassignDrawer(assignment?.id)}
                    >
                      Unassign
                    </MDButton>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1.1}>
                    <MDTypography variant="body2" sx={{ color: ui.body }}>
                      Assigned on: <strong>{formatDate(assignment?.assigned_date)}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: ui.body }}>
                      Purchase date: <strong>{formatDate(hardware?.purchase_date)}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: ui.body }}>
                      Warranty till: <strong>{formatDate(hardware?.warranty_till)}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: ui.body }}>
                      Purchased from: <strong>{hardware?.purchased_from || "Not available"}</strong>
                    </MDTypography>
                    {hardware?.remarks ? (
                      <MDTypography variant="body2" sx={{ color: ui.body }}>
                        Remarks: <strong>{hardware.remarks}</strong>
                      </MDTypography>
                    ) : null}
                  </Stack>

                  {/*<Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                    {hardware?.qr_code ? (
                      <MDButton
                        size="small"
                        variant="text"
                        color="info"
                        component="a"
                        href={hardware.qr_code}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<QrCode2 />}
                      >
                        View QR
                      </MDButton>
                    ) : null}
                    <MDButton size="small" variant="text" color="dark" disabled startIcon={<PersonAddAlt1 />}>
                      Assigned in profile
                    </MDButton>
                  </Stack>*/}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Card>
  );
};

export default SystemDetails;
